import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { get, round, cloneDeep } from 'lodash-es';

import { ValueConversionService } from './value-conversion.service';
import { AppSettingsService } from './app-settings.service';
/**
 * A helper service for value operations (e.g. Value comparison)
 */
@Injectable({
  providedIn: 'root'
})
export class ValueService {


  constructor(private converter: ValueConversionService, private http: HttpClient, private appSettings: AppSettingsService) {}

  /**
   * Convienence method that wrapps the getBoolean of the from '../value-conversion.service'
   *
   */
  public getBoolean(v: any) {
    return this.converter.getBoolean(v);
  }

  /**
   * Tests 2 string values for equality, ignoring case
   */
  public equalsIgnoringCase(s1: string, s2: string): boolean {
    if (s1 === undefined || s2 === undefined) {
      return false;
    }

    // treat apostrophe and single quote the same
    const s1x = s1.replace('’', "'");
    const s2x = s2.replace('’', "'");

    return s1x.localeCompare(s2x, undefined, { sensitivity: 'base' }) === 0;
  }

  /**
   * Gets a value from an object via path.  Uses lodash _.get
   */
  public getSafeValue(object: any, path: string) {
    // https://lodash.com/docs/4.17.15#get
    const safe = get(object, path);

    return safe;
  }

  /**
   * Finds an actual key value for a key value of possibily differing casing.
   * This method will throw an error if there are multiple matches.
   * @param o
   * @param key
   */
  public findKey(o: any, key: string): string {
    let matchingKeys = Object.keys(o).filter((k) => k.toLowerCase() === key.toLowerCase());

    if (matchingKeys.length === 0) {
      return null;
    }

    if (matchingKeys.length === 1) {
      return matchingKeys[0];
    } else {
      
      throw `Multiple properties found for key:${key} (${matchingKeys})`;
    }
  }

  /**
   * Checks a value to see if it is a valid email address
   */
  public isValidEmail(val: string) {
    const re =
      /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

    // Uniocde example
    // const re = /^(([^<>()\[\]\.,;:\s@\"]+(\.[^<>()\[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,})$/i;

    return re.test(String(val).toLowerCase());
  }

  /**
   * Checks a value to see if it is a valid phone number
   * @param phoneNumber
   */
  public isValidPhoneNumber(phoneNumber: string): boolean {
    if (phoneNumber) {
      const x = phoneNumber.replace(/\D/g, '');

      if (x.length === 10) {
        return true;
      } else {
        return false;
      }
    }

    return true;
  }

  /**
   * Checks a value to see if it is a valid phone number and remove non numeric chars
   * @param phoneNumber
   */
  public convertValidPhoneNumber(phoneNumber: string): string {
    if (phoneNumber) {
      const x = phoneNumber.replace(/\D/g, '');
      if (x.length === 10) {
        return x;
      } else {
        return 'not a valid phone number';
      }
    }
  }

  /**
   * Checks a value to see if it is numeric
   */
  public isNumeric(val: any): boolean {
    if (val === null || val === undefined) {
      return false;
    }
    return !isNaN(val) && !isNaN(parseFloat(val));
  }

  /**
   * Rounds a decimal up to the nearest whole number
   */
  public roundDecimalUp(v: number): number {
    return Math.ceil(v);
  }

  /**
   * Rounds a number (wraps lodash   https://lodash.com/docs/4.17.15#round)
   * @param v
   * @param precision
   * @returns
   */
  public round(v: number, precision: number = 0): number {
    return round(v, precision);
  }

  /**
   * Checks to see if a number value has a fraction(decimal).
   * @param inputValue
   */
  hasFraction(inputValue: any) {
    let v = +inputValue;

    return v % 1 !== 0;
  }

  /**
   * Generates a new universally unique ID.
   */
  public generateUUID() {
    // Public Domain/MIT
    var d = new Date().getTime();
    if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
      d += performance.now(); //use high-precision timer if available
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = (d + Math.random() * 16) % 16 | 0;
      d = Math.floor(d / 16);
      return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });
  }

  /**
   * Converts and azimuth value to a compass direction N,S,E,W
   */
  public convertAzimuthToCompassDirection(azimuth: number) {
    // TODO: Add NNE,NNW,ESE,etc

    if (azimuth >= 0 && azimuth <= 360) {
      if (azimuth >= 0 && azimuth < 45) {
        return 'North';
      } else if (azimuth >= 45 && azimuth < 135) {
        return 'East';
      } else if (azimuth >= 135 && azimuth < 225) {
        return 'South';
      } else if (azimuth >= 225 && azimuth < 315) {
        return 'West';
      } else if (azimuth >= 315 && azimuth <= 360) {
        return 'North';
      }
    } else {
      return null;
    }
  }

  /**
   * Compares 2 objects by converting them to strings and comparing the strings.
   * @param value1
   * @param value2
   * @returns
   */
  public isObjectEqual(value1: object, value2: object) {
    const s1 = JSON.stringify(value1);
    const s2 = JSON.stringify(value2);

    return s1 === s2;
  }

  /**
   * Round a value up to the nearest 500
   * @param totalLOAmount
   */
  public roundUp500(num: number): number {
    var x1 = Math.ceil(num / 500) * 500;
    console.log('x1: ', x1);

    return x1;
  }

  /**
   * Round a value up to the nearest 100
   * @param totalLOAmount
   */
  public roundUp100(num: number): number {
    var x1 = Math.ceil(num / 100) * 100;
    console.log('x1: ', x1);

    return x1;
  }

  /**
   * Gets the similarity of 2 strings.  The result is between 0 and 1 where 0 is not similar and 1 is identifical.
   * @param string1
   * @param string2
   */
  public getSimilarity(string1: string, string2: string) {
    return this.http.get<number>(`${this.appSettings.outdoorAccessPiebaldApiUrl}/sim-metric/${string1}/${string2}`);
  }

  public formatInternationalPhoneNumber(value: string, digitsOnly: boolean) {
    let x = new String(value);

    if (digitsOnly) {
      // http://www.regular-expressions.info/reference.html
      x = x.replace(/\D/g, '');

      if (x.length === 10) {
        return `+1${x}`;
      } else if (x.length === 11) {
        return `+${x}`;
      } else if (x.length === 12) {
        return x;
      }
    } else {
      throw new Error('Not Implemented');
    }
  }

  public cloneDeep(objectOrArray:any ):any {
    return cloneDeep(objectOrArray);
  }
  /**
   * Does string1 include string2?
   * @param string1
   * @param string2
   */
  public includes(string1: string, string2: string, caseInsensitive: boolean) {
    if (!string1) return false;

    if (caseInsensitive) {
      return string1.toLowerCase().includes(string2.toLowerCase());
    }

    return string1.includes(string2);
  }

  /**
   * Tests a string array for a value
   * @param values
   * @param s
   * @returns
   */
  public contains(values: string[], s: string, caseInsensitive: boolean) {
    let b = false;

    if (values && values.length > 0) {
      values.forEach((v) => {
        if (caseInsensitive) {
          if (this.equalsIgnoringCase(v, s)) {
            b = true;
          }
        } else {
          b = v === s;
        }
      });
    }

    return b;
  }

  /**
   * Parses the enum values 
   * @param values  Output from Object.values(T)
   */
  public getEnumValues( values: any[]): string[] {
    return  values.slice(0,values.length / 2);
  }
}
